import { SxProps, Theme } from '@mui/material';
import { resolveTypographyVariantStyles } from '@aspendental/themes';

import { Sizes } from '@/types';
import { Maybe } from '@/types/generated';
import { IImageAsset } from '@/components/ImageAsset/ImageAsset';

export const wrapper = {
	display: 'block',
	margin: '0 auto',
	'& [data-testid="group_multicards_cards"]': {
		maxWidth: 'unset',
	},
};

export const backgroundImageParsed = (
	{ desktopImage, mobileImage }: IImageAsset,
	fixBackgroundToBottom?: Maybe<boolean>,
	isSmallScreen?: Maybe<boolean>
): object => {
	if (!desktopImage && !mobileImage) return {};

	const backgroundPosition = fixBackgroundToBottom ? 'bottom center' : 'center';
	const backgroundSize = fixBackgroundToBottom ? 'auto' : 'cover';
	const displayImage = isSmallScreen && mobileImage?.image ? mobileImage : desktopImage;
	const imageUrl = displayImage?.image?.cdn_url || '';

	return {
		backgroundPosition,
		backgroundSize,
		backgroundImage: `url(${imageUrl})`,
		backgroundRepeat: 'no-repeat',
	};
};

export const backgroundContainerStyles = (
	backgroundImage?: Maybe<IImageAsset>,
	fixBackgroundToBottom?: Maybe<boolean>,
	isSmallScreen?: Maybe<boolean>
): object => {
	return {
		paddingY: '3.75rem',
		...(backgroundImage && backgroundImageParsed(backgroundImage, fixBackgroundToBottom, isSmallScreen)),
	};
};

export const getHeaderSpacingValue = (headerSpacing: Maybe<Sizes> = 'Medium') => {
	const headerSpacingValues = {
		Small: 1.5,
		Medium: 2.5,
		Large: 4,
		XLarge: 6,
	} as { [key: string]: number };

	if (!headerSpacing) {
		return headerSpacingValues['Medium'];
	}

	return headerSpacingValues[headerSpacing];
};

export const getSectionWrapperStyles = (isSmallScreen: boolean, sectionAlignment?: Maybe<string>): SxProps => {
	return {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: sectionAlignment ?? 'flex-start',
		alignItems: isSmallScreen ? 'flex-start' : 'center',
		textAlign: sectionAlignment === 'center' ? 'center' : 'left',
		marginBottom: isSmallScreen ? '2.5rem' : '3.75rem',
		gap: '0.75rem',
		marginLeft: sectionAlignment === 'flex-start' ? 0 : 'auto',
	};
};

export const getEyebrowStyles = (theme: Theme) => ({
	lineHeight: '1rem',
	fontFamily: 'Messina Sans',
	fontSize: '0.875rem',
	fontWeight: 700,
	letterSpacing: '0.188rem',
	marginBottom: '1rem',
	[theme.breakpoints.down('sm')]: {
		lineHeight: '1rem',
		fontSize: '0.875rem',
		fontWeight: 700,
		fontFamily: 'Messina Sans',
		letterSpacing: '0.188rem',
		marginBottom: '1rem',
	},
});

export const getCardsWrapperStyles = ({
	isDarkBackground,
	cardsAlignment,
	numberOfCards,
	shouldDisplayCarouselOnDesktop,
}: {
	isDarkBackground: boolean;
	cardsAlignment?: Maybe<string>;
	numberOfCards: number;
	shouldDisplayCarouselOnDesktop?: Maybe<boolean>;
}) => ({
	display: shouldDisplayCarouselOnDesktop ? 'flex' : 'grid',
	gridTemplateColumns: { xs: 'repeat(1, 1fr)', md: `repeat(${numberOfCards}, ${numberOfCards}fr)` },
	gridColumnGap: '.75rem',
	gridRowGap: '1rem',
	alignItems: 'stretch',
	alignContent: 'center',
	justifyContent: 'center',
	gap: 0,
	flexWrap: 'wrap',
	boxSizing: 'border-box',
	padding: '0',
	textAlign: cardsAlignment || 'left',
	...(isDarkBackground
		? {
				'& .MuiTypography-root': {
					color: 'text.light',
				},
		  }
		: {}),
});

export const getHeadingStyles = (theme: Theme) => ({
	...resolveTypographyVariantStyles(theme, 'header1'),
	textAlign: 'center' as const,
	fontFamily: 'Messina Modern',
	lineHeight: '3.75rem',
	fontSize: '3.75rem',
	fontWeight: 300,
	[theme.breakpoints.down('md')]: {
		textAlign: 'left' as const,
		fontSize: '2.625rem',
		lineHeight: '3rem',
	},
	[theme.breakpoints.down('sm')]: {
		textAlign: 'left' as const,
		fontSize: '2.625rem',
		lineHeight: '3rem',
	},
});

export const richTextDescStyles = (isSmallScreen: boolean) => ({
	'& p': {
		fontSize: isSmallScreen ? '1.5rem' : '1.875rem',
		lineHeight: isSmallScreen ? '2rem' : '2.5rem',
	},
});

export const getMultiCardButtonsStyles = (isSmallScreen: boolean) => ({
	display: 'flex',
	alignContent: 'center',
	justifyContent: 'center',
	gap: '1rem',
	mt: isSmallScreen ? 0 : '3.75rem',
	flexWrap: 'wrap',
});

export const multiCardChipStyles = {
	color: 'secondary.main',
	borderColor: 'secondary.main',
	'&:hover': {
		color: 'secondary.main',
		backgroundColor: 'neutrals.white',
		opacity: 0.8,
	},
	'&.Mui-selected': {
		backgroundColor: 'secondary.main',
		color: 'neutrals.white',
		'&:hover': {
			color: 'neutrals.white',
			backgroundColor: 'secondary.main',
			opacity: 0.8,
		},
	},
};
